import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Heading,
  Text,
  Button,
  VStack,
  HStack,
  Card,
  CardHeader,
  CardBody,
  Badge,
  Divider,
  Input,
  FormControl,
  FormLabel,
  FormHelperText,
  Switch,
  useToast,
  Spinner,
  Alert,
  AlertIcon,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Select,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
  IconButton,
  Flex,
  Spacer,
  Tooltip,
} from "@chakra-ui/react";
import { FiRefreshCw, FiCalendar, FiAlertCircle, FiCheck, FiX, FiLink, FiUsers, FiArrowLeft } from "react-icons/fi";
import { useUser, useFindMany, useAction, useActionForm } from "@gadgetinc/react";
import { api } from "../api";

export default function AdminCalendarSync() {
  const navigate = useNavigate();
  const user = useUser();
  const toast = useToast();
  const [syncing, setSyncing] = useState({});
  const [syncResults, setSyncResults] = useState({});
  const [editingCohort, setEditingCohort] = useState(null);
  const [calendarUrl, setCalendarUrl] = useState("");
  const { isOpen: isUnmatchedOpen, onOpen: onUnmatchedOpen, onClose: onUnmatchedClose } = useDisclosure();
  const [unmatchedData, setUnmatchedData] = useState(null);
  const [, syncCalendarAction] = useAction(api.syncFisdapCalendar);

  // Fetch cohorts for the user's institution
  const [{ data: cohorts, fetching: loadingCohorts, error: cohortsError }, refetchCohorts] = useFindMany(api.cohort, {
    filter: {
      isActive: { equals: true },
    },
    select: {
      id: true,
      name: true,
      displayName: true,
      cohortType: true,
      institution: true,
      fisdapCalendarUrl: true,
      calendarSyncEnabled: true,
      lastCalendarSyncAt: true,
      calendarSyncStatus: true,
      calendarSyncError: true,
      currentEnrollment: true,
    },
    sort: { name: "Ascending" },
  });

  const handleSyncNow = useCallback(async (cohortId) => {
    setSyncing((prev) => ({ ...prev, [cohortId]: true }));
    try {
      const payload = {
        cohortId,
        forceSync: true,
      };
      if (user?.id) payload.initiatedByUserId = user.id;

      const data = await syncCalendarAction(payload);

      if (data?.results) {
        const result = data.results?.[0];
        setSyncResults((prev) => ({ ...prev, [cohortId]: result }));
        toast({
          title: "Sync Complete",
          description: `Created: ${result?.created || 0}, Updated: ${result?.updated || 0}, Cancelled: ${result?.cancelled || 0}`,
          status: "success",
          duration: 5000,
        });
        refetchCohorts();
      } else {
        toast({
          title: "Sync Failed",
          description: "Unknown error",
          status: "error",
          duration: 5000,
        });
      }
    } catch (err) {
      toast({
        title: "Sync Error",
        description: err.message,
        status: "error",
        duration: 5000,
      });
    } finally {
      setSyncing((prev) => ({ ...prev, [cohortId]: false }));
    }
  }, [syncCalendarAction, user?.id, toast, refetchCohorts]);

  const handleSaveCalendarUrl = useCallback(async (cohortId) => {
    try {
      await api.cohort.update(cohortId, {
        fisdapCalendarUrl: calendarUrl || null,
      });
      setEditingCohort(null);
      setCalendarUrl("");
      toast({ title: "Calendar URL saved", status: "success", duration: 3000 });
      refetchCohorts();
    } catch (err) {
      toast({ title: "Error saving URL", description: err.message, status: "error", duration: 5000 });
    }
  }, [calendarUrl, toast, refetchCohorts]);

  const handleToggleSync = useCallback(async (cohortId, enabled) => {
    try {
      await api.cohort.update(cohortId, {
        calendarSyncEnabled: enabled,
      });
      refetchCohorts();
    } catch (err) {
      toast({ title: "Error toggling sync", description: err.message, status: "error", duration: 5000 });
    }
  }, [toast, refetchCohorts]);

  const handleViewUnmatched = useCallback((cohortId) => {
    const result = syncResults[cohortId];
    if (result?.unmatchedStudents?.length > 0) {
      setUnmatchedData({ cohortId, students: result.unmatchedStudents });
      onUnmatchedOpen();
    }
  }, [syncResults, onUnmatchedOpen]);

  const getSyncStatusBadge = (status) => {
    const map = {
      idle: { colorScheme: "gray", label: "Idle" },
      syncing: { colorScheme: "blue", label: "Syncing..." },
      success: { colorScheme: "green", label: "Success" },
      error: { colorScheme: "red", label: "Error" },
    };
    const config = map[status] || map.idle;
    return <Badge colorScheme={config.colorScheme}>{config.label}</Badge>;
  };

  return (
    <Box minH="100vh" bg="gray.900" color="white" p={6}>
      <Container maxW="7xl">
        <HStack mb={6}>
          <IconButton
            icon={<FiArrowLeft />}
            aria-label="Back"
            variant="ghost"
            color="white"
            onClick={() => navigate("/admin")}
          />
          <Box>
            <Heading size="lg">
              <HStack><FiCalendar /><Text>FISDAP Calendar Sync</Text></HStack>
            </Heading>
            <Text color="gray.400" mt={1}>
              Manage iCal calendar subscriptions for automatic shift import from FISDAP
            </Text>
          </Box>
        </HStack>

        {cohortsError && (
          <Alert status="error" mb={4} borderRadius="md">
            <AlertIcon />
            Error loading cohorts: {cohortsError.message}
          </Alert>
        )}

        {loadingCohorts ? (
          <Flex justify="center" py={10}>
            <Spinner size="xl" color="blue.400" />
          </Flex>
        ) : (
          <VStack spacing={4} align="stretch">
            {cohorts?.map((cohort) => (
              <Card key={cohort.id} bg="gray.800" borderColor="gray.700" borderWidth="1px">
                <CardHeader pb={2}>
                  <Flex align="center">
                    <Box>
                      <HStack>
                        <Heading size="sm" color="white">{cohort.displayName || cohort.name}</Heading>
                        <Badge colorScheme="purple">{cohort.cohortType}</Badge>
                        {getSyncStatusBadge(cohort.calendarSyncStatus)}
                      </HStack>
                      <Text fontSize="sm" color="gray.400" mt={1}>
                        {cohort.currentEnrollment || 0} students enrolled
                        {cohort.lastCalendarSyncAt && (
                          <> &bull; Last synced: {new Date(cohort.lastCalendarSyncAt).toLocaleString()}</>
                        )}
                      </Text>
                    </Box>
                    <Spacer />
                    <HStack>
                      <FormControl display="flex" alignItems="center" w="auto">
                        <FormLabel htmlFor={`sync-${cohort.id}`} mb="0" fontSize="sm" color="gray.400" mr={2}>
                          Auto-sync
                        </FormLabel>
                        <Switch
                          id={`sync-${cohort.id}`}
                          isChecked={cohort.calendarSyncEnabled}
                          onChange={(e) => handleToggleSync(cohort.id, e.target.checked)}
                          colorScheme="blue"
                          isDisabled={!cohort.fisdapCalendarUrl}
                        />
                      </FormControl>
                      <Button
                        leftIcon={<FiRefreshCw />}
                        size="sm"
                        colorScheme="blue"
                        isLoading={syncing[cohort.id]}
                        loadingText="Syncing..."
                        isDisabled={!cohort.fisdapCalendarUrl}
                        onClick={() => handleSyncNow(cohort.id)}
                      >
                        Sync Now
                      </Button>
                    </HStack>
                  </Flex>
                </CardHeader>
                <CardBody pt={2}>
                  {/* Calendar URL */}
                  {editingCohort === cohort.id ? (
                    <HStack>
                      <Input
                        placeholder="https://fisdap.net/scheduler/ical/..."
                        value={calendarUrl}
                        onChange={(e) => setCalendarUrl(e.target.value)}
                        bg="gray.700"
                        borderColor="gray.600"
                        size="sm"
                      />
                      <Button size="sm" colorScheme="green" onClick={() => handleSaveCalendarUrl(cohort.id)}>
                        Save
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => { setEditingCohort(null); setCalendarUrl(""); }}>
                        Cancel
                      </Button>
                    </HStack>
                  ) : (
                    <HStack>
                      <FiLink color="gray" />
                      <Text fontSize="sm" color={cohort.fisdapCalendarUrl ? "blue.300" : "gray.500"} isTruncated maxW="500px">
                        {cohort.fisdapCalendarUrl || "No calendar URL configured"}
                      </Text>
                      <Button
                        size="xs"
                        variant="outline"
                        colorScheme="blue"
                        onClick={() => {
                          setEditingCohort(cohort.id);
                          setCalendarUrl(cohort.fisdapCalendarUrl || "");
                        }}
                      >
                        {cohort.fisdapCalendarUrl ? "Edit" : "Set URL"}
                      </Button>
                    </HStack>
                  )}

                  {/* Sync error display */}
                  {cohort.calendarSyncError && (
                    <Alert status="error" mt={3} borderRadius="md" size="sm">
                      <AlertIcon />
                      <Text fontSize="sm">{cohort.calendarSyncError}</Text>
                    </Alert>
                  )}

                  {/* Sync results */}
                  {syncResults[cohort.id] && (
                    <Box mt={3} p={3} bg="gray.750" borderRadius="md" borderWidth="1px" borderColor="gray.600">
                      <Text fontSize="sm" fontWeight="bold" mb={2}>Last Sync Results:</Text>
                      <HStack spacing={4} flexWrap="wrap">
                        <Badge colorScheme="green">Created: {syncResults[cohort.id].created}</Badge>
                        <Badge colorScheme="blue">Updated: {syncResults[cohort.id].updated}</Badge>
                        <Badge colorScheme="orange">Cancelled: {syncResults[cohort.id].cancelled}</Badge>
                        <Badge colorScheme="gray">Skipped: {syncResults[cohort.id].skipped}</Badge>
                        {syncResults[cohort.id].unmatchedStudents?.length > 0 && (
                          <Button
                            size="xs"
                            colorScheme="yellow"
                            variant="outline"
                            leftIcon={<FiUsers />}
                            onClick={() => handleViewUnmatched(cohort.id)}
                          >
                            {syncResults[cohort.id].unmatchedStudents.length} Unmatched Students
                          </Button>
                        )}
                      </HStack>
                    </Box>
                  )}
                </CardBody>
              </Card>
            ))}

            {(!cohorts || cohorts.length === 0) && (
              <Card bg="gray.800" borderColor="gray.700" borderWidth="1px">
                <CardBody textAlign="center" py={10}>
                  <Text color="gray.400">No active cohorts found. Create a cohort first to set up calendar sync.</Text>
                </CardBody>
              </Card>
            )}
          </VStack>
        )}

        {/* Unmatched Students Modal */}
        <Modal isOpen={isUnmatchedOpen} onClose={onUnmatchedClose} size="lg">
          <ModalOverlay />
          <ModalContent bg="gray.800" color="white">
            <ModalHeader>Unmatched Students</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <Text fontSize="sm" color="gray.400" mb={4}>
                These student names from the FISDAP calendar could not be matched to user accounts.
                They may need to be manually linked or the students need to create accounts.
              </Text>
              <TableContainer>
                <Table size="sm">
                  <Thead>
                    <Tr>
                      <Th color="gray.400">Name</Th>
                      <Th color="gray.400">Shift Date</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {unmatchedData?.students?.map((s, i) => (
                      <Tr key={i}>
                        <Td>{s.studentName}</Td>
                        <Td>{s.shiftDate ? new Date(s.shiftDate).toLocaleDateString() : "—"}</Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </TableContainer>
            </ModalBody>
            <ModalFooter>
              <Button onClick={onUnmatchedClose}>Close</Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </Container>
    </Box>
  );
}
